import React, { useEffect, useRef } from 'react';

const ROWS = 24;
const COLS = 32;
// Temperature range for body/animal thermal monitoring
const CLIP_MIN = 15.0;
const CLIP_MAX = 42.0;
const OUTPUT_WIDTH = 640;
const OUTPUT_HEIGHT = 480;
const INVALID_TEMP_THRESHOLD = -40; // Filter out sensor errors

// Thermal palette - RED (cold/env) to YELLOW (body) to WHITE (hot)
// Mapping for CLIP_MIN=15, CLIP_MAX=42:
// 15°C → t=0.0 (dark red - cold)
// 24°C → t=0.26 (red - room temp)
// 32°C → t=0.52 (orange - warm object)
// 36.5°C → t=0.68 (bright yellow) ← normal body temp
// 38°C → t=0.73 (bright yellow) ← fever
// 40°C → t=0.81 (yellow-orange) ← high fever
// 42°C → t=1.0 (white) ← extreme
const THERMAL_STOPS: Array<{ t: number; rgb: [number, number, number] }> = [
  { t: 0.0, rgb: [100, 0, 0] },          // Dark red (15°C - cold)
  { t: 0.15, rgb: [180, 20, 0] },        // Deep red (~19°C)
  { t: 0.3, rgb: [255, 60, 0] },         // Red (~24°C - room temp)
  { t: 0.45, rgb: [255, 100, 0] },       // Orange-red (~27°C)
  { t: 0.52, rgb: [255, 150, 0] },       // Orange (~29°C)
  { t: 0.6, rgb: [255, 200, 0] },        // Orange-yellow (~34°C)
  { t: 0.68, rgb: [255, 230, 0] },       // Bright yellow (~36.5°C - normal body)
  { t: 0.73, rgb: [255, 240, 50] },      // Bright yellow (~38°C - fever)
  { t: 0.81, rgb: [255, 220, 120] },     // Yellow-white (~40°C)
  { t: 1.0, rgb: [255, 255, 255] },      // White (42°C - extreme)
];

const clipTemp = (value: number): number => Math.min(CLIP_MAX, Math.max(CLIP_MIN, value));

export const isValidTemp = (value: number): boolean => value > INVALID_TEMP_THRESHOLD;

const toNormalized8bit = (value: number): number => {
  const clipped = clipTemp(value);
  const normalized = ((clipped - CLIP_MIN) / (CLIP_MAX - CLIP_MIN)) * 255;
  return Math.max(0, Math.min(255, Math.round(normalized)));
};

const thermalColor = (value8bit: number): [number, number, number] => {
  const t = value8bit / 255;

  let left = THERMAL_STOPS[0];
  let right = THERMAL_STOPS[THERMAL_STOPS.length - 1];

  for (let i = 0; i < THERMAL_STOPS.length - 1; i += 1) {
    const a = THERMAL_STOPS[i];
    const b = THERMAL_STOPS[i + 1];
    if (t >= a.t && t <= b.t) {
      left = a;
      right = b;
      break;
    }
  }

  const span = right.t - left.t || 1;
  const localT = (t - left.t) / span;

  return [
    Math.round(left.rgb[0] + (right.rgb[0] - left.rgb[0]) * localT),
    Math.round(left.rgb[1] + (right.rgb[1] - left.rgb[1]) * localT),
    Math.round(left.rgb[2] + (right.rgb[2] - left.rgb[2]) * localT),
  ];
};

interface ThermalGridProps {
  /** The 24x32 temperature grid. */
  gridData: number[];
  className?: string;
}

/**
 * The thermal heatmap canvas, on its own.
 *
 * Split out of `dashboard/ThermalPanel.tsx` so the cat detail page can draw a
 * grid of its own without a Redux store behind it — that panel reads the live
 * sensor data from the store, and the cat page has no sensor yet.
 */
export const ThermalGrid: React.FC<ThermalGridProps> = ({ gridData, className = '' }) => {
  const heatmapCanvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const outputCanvas = heatmapCanvasRef.current;
    if (!outputCanvas || gridData.length !== ROWS * COLS) return;

    outputCanvas.width = OUTPUT_WIDTH;
    outputCanvas.height = OUTPUT_HEIGHT;

    const outputCtx = outputCanvas.getContext('2d');
    if (!outputCtx) return;

    const sourceCanvas = document.createElement('canvas');
    sourceCanvas.width = COLS;
    sourceCanvas.height = ROWS;
    const sourceCtx = sourceCanvas.getContext('2d');
    if (!sourceCtx) return;

    const img = sourceCtx.createImageData(COLS, ROWS);
    for (let i = 0; i < gridData.length; i += 1) {
      const temp = gridData[i];
      // Use 0 (black) for invalid temperatures, otherwise map to color
      let r = 0, g = 0, b = 0;
      if (isValidTemp(temp)) {
        const value8bit = toNormalized8bit(temp);
        [r, g, b] = thermalColor(value8bit);
      }
      const pixel = i * 4;
      img.data[pixel] = r;
      img.data[pixel + 1] = g;
      img.data[pixel + 2] = b;
      img.data[pixel + 3] = 255;
    }

    sourceCtx.putImageData(img, 0, 0);

    // Browser canvas doesn't expose cv2.INTER_CUBIC directly.
    // High-quality smoothing produces similar cubic-style interpolation.
    outputCtx.clearRect(0, 0, OUTPUT_WIDTH, OUTPUT_HEIGHT);
    outputCtx.imageSmoothingEnabled = true;
    outputCtx.imageSmoothingQuality = 'high';
    outputCtx.drawImage(sourceCanvas, 0, 0, OUTPUT_WIDTH, OUTPUT_HEIGHT);
  }, [gridData]);

  return (
    <canvas
      ref={heatmapCanvasRef}
      className={`w-full rounded-lg border border-gray-300 dark:border-gray-700 ${className}`}
      style={{
        maxHeight: '100%',
        aspectRatio: `${OUTPUT_WIDTH}/${OUTPUT_HEIGHT}`,
        imageRendering: 'auto',
      }}
    />
  );
};

export { CLIP_MIN, CLIP_MAX, ROWS as THERMAL_ROWS, COLS as THERMAL_COLS };
export default ThermalGrid;
